import { useEffect } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useThemeStore } from '@/shared/hooks/useTheme.ts';
import { usePublicStatus, usePublicUptime, usePublicIncidents, usePublicMaintenances } from './hooks.ts';
import { HeroBanner } from './components/HeroBanner.tsx';
import { StatusBars } from './components/StatusBars.tsx';
import { ActiveIncidents } from './components/ActiveIncidents.tsx';
import { ScheduledMaint } from './components/ScheduledMaint.tsx';
import { IncidentHistory } from './components/IncidentHistory.tsx';

export function StatusPage() {
  const { theme, toggle } = useThemeStore();
  const { data: statusData, isLoading: loadingStatus } = usePublicStatus();
  const { data: uptimeData } = usePublicUptime();
  const { data: incidentsData } = usePublicIncidents();
  const { data: maintenancesData } = usePublicMaintenances();

  // SEO: título dinâmico da aba
  useEffect(() => {
    if (statusData?.tenant.nome) {
      document.title = `Status — ${statusData.tenant.nome}`;
    }
    return () => { document.title = 'Woli Uptime Monitor'; };
  }, [statusData?.tenant.nome]);

  if (loadingStatus || !statusData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-sm text-gray-400">Carregando...</div>
      </div>
    );
  }

  // Contar incidentes dos últimos 30 dias (abertos + histórico)
  const now = Date.now();
  const thirtyDaysAgo = now - 30 * 86_400_000;
  const incidentes30d = incidentsData
    ? [...incidentsData.abertos, ...incidentsData.historico].filter(
        (i) => new Date(i.iniciadoEm).getTime() >= thirtyDaysAgo,
      ).length
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 font-sans dark:bg-gray-950">
      {/* Header sticky */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/80 backdrop-blur dark:border-gray-800 dark:bg-gray-900/80">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{
                backgroundColor:
                  statusData.status === 'UP'
                    ? 'var(--color-status-up)'
                    : statusData.status === 'DOWN'
                      ? 'var(--color-status-down)'
                      : 'var(--color-status-degraded)',
              }}
            />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {statusData.tenant.nome}
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                {statusData.tenant.slug}.woli.com.br
              </span>
            </span>
          </div>
          <button
            onClick={toggle}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Alternar tema"
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
        </div>
      </header>

      {/* Conteúdo */}
      <main className="mx-auto max-w-3xl space-y-6 px-4 py-8">
        <HeroBanner
          tenantNome={statusData.tenant.nome}
          tenantSlug={statusData.tenant.slug}
          status={statusData.status}
          uptimeMes={uptimeData?.uptimeMesAtual ?? null}
          uptime90dias={uptimeData?.uptime90dias ?? null}
          uptime12meses={uptimeData?.uptime12meses ?? null}
          incidentes30d={incidentes30d}
          latenciaMs={statusData.latenciaMs}
        />

        {uptimeData && (
          <StatusBars
            historico={uptimeData.historico}
            historicoHoras={uptimeData.historicoHoras ?? []}
          />
        )}

        {incidentsData && <ActiveIncidents incidents={incidentsData.abertos} />}

        {maintenancesData && <ScheduledMaint maintenances={maintenancesData.agendadas} />}

        {incidentsData && incidentsData.historico.length > 0 && (
          <IncidentHistory incidents={incidentsData.historico} />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-6 text-center text-xs text-gray-400 dark:border-gray-800 dark:text-gray-500">
        <p>Monitorado por Woli Uptime Monitor</p>
        <p className="mt-1">&copy; {new Date().getFullYear()} Woli Tecnologia. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
}
